import React, { useCallback } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { UnitRole } from '../../../../types/unit-roles'
import { getUnitRole } from '../../../../helpers/translations/unit-roles'
import { FormSpacing } from '../../../common/FormSpacing'
import {
  societyUserUnitsUpdateSchema,
  societyUserUnitUpdateSchema,
} from '../../../../forms/schemas/society_user_unit_update'
import { Divider } from '../../../common/Divider'
import { IUserModel } from '../../../../interfaces/models/users.interfaces'
import { entranceQueries } from '../../../../api/society-entrances'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { unitQueries } from '../../../../api/units'
import { i18nSocietyType } from '../../../../i18n'

interface EditResidentUnitsFormProps {
  onError: () => void
  onSubmit: (
    data: InferType<typeof societyUserUnitsUpdateSchema>,
    existingUserUnits: InferType<typeof societyUserUnitUpdateSchema>[]
  ) => void
  loading: boolean
  user: IUserModel
  society: ISocietiesModel
}

export const EditResidentUnitsForm = ({
  loading,
  onError,
  onSubmit,
  user,
  society,
}: EditResidentUnitsFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()

  const { data: societyEntrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society._id)
  )
  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society._id)
  )

  const getUserUnits = useCallback(() => {
    return (
      societyUnits
        ?.filter((unit) => !unit.isUnassigned)
        .filter((unit) => {
          return unit.residentsList?.find(
            (resident) => resident.userId === user._id
          )
        }) || []
    )
  }, [user, societyUnits])

  const userUnits = getUserUnits()
  const existingUserUnits = userUnits.map((unit) => {
    return {
      _id: unit._id,
      societyId: society._id,
      entranceId: unit.entranceId,
      unitIdOrTitle: unit._id,
      userId: user._id,
      unitRole:
        unit?.residentsList?.find((resident) => resident.userId === user._id)
          ?.role || '',
    }
  })

  const getDefaultValues = useCallback((): InferType<
    typeof societyUserUnitsUpdateSchema
  > => {
    return {
      // TODO: How to handle "forced" types that do not conform to yup?
      // In this case, we want to force some undefined.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      units:
        userUnits.length === 0
          ? [
              {
                _id: undefined,
                societyId: society._id,
                entranceId:
                  societyEntrances && societyEntrances.length > 0
                    ? societyEntrances[0]._id
                    : undefined,
                unitIdOrTitle: undefined,
                userId: user._id,
                unitRole: UnitRole.MEMBER as string,
              },
            ]
          : existingUserUnits,
    }
  }, [society, societyEntrances, userUnits, existingUserUnits, user._id])
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyUserUnitsUpdateSchema),
    defaultValues: getDefaultValues(),
  })

  const { fields, append, remove } = useFieldArray({
    name: 'units',
    control,
  })

  const watchUnits = watch('units')
  const availableUnitsForWatchedUnit = watchUnits.map(
    (_unit) =>
      societyUnits
        ?.filter((unit) => {
          const entrance =
            unit.entranceId &&
            societyEntrances?.find(
              (_entrance) => _entrance._id === unit.entranceId
            )
          return entrance ? entrance._id === _unit.entranceId : false
        })
        .map((unit) => {
          if (unit.isUnassigned) {
            return {
              value: unit._id,
              label: translate('common.unit.unassigned'),
            }
          }
          return {
            value: unit._id,
            label: `${unit.titleLegal} (${unit.title})`,
          }
        }) || []
  )

  const entranceOptions =
    societyEntrances?.map((_entrance) => ({
      label: _entrance.addressStreet as string,
      value: _entrance._id,
    })) || []

  const unitRoleOptions = Object.values(UnitRole).map((val) => ({
    value: val as string,
    label: translate(getUnitRole(val) as string),
  }))

  const _onSubmit = async (
    data: InferType<typeof societyUserUnitsUpdateSchema>
  ): Promise<void> => {
    onSubmit(data, existingUserUnits)
  }

  return (
    <FormSpacing>
      {fields.map((field, index) => (
        <FormSpacing key={field._id}>
          <Controller
            control={control}
            name={`units.${index}.entranceId`}
            render={({ field: { value, name, onChange } }) => (
              <FormControl
                label={translate('registerView.unit.labels.entrance', {
                  context: i18nSocietyType(society.societyTypeDisplayName),
                })}
                error={
                  errors.units?.[index]?.entranceId &&
                  getErrorMessage(errors.units[index]?.entranceId)
                }
                name={name}
              >
                <SelectDropdown
                  options={entranceOptions}
                  onChange={(option) => {
                    onChange(option?.value)
                    const updatedUnits = watchUnits.map((_unit, _index) =>
                      _index === index ? { ..._unit, unitIdOrTitle: '' } : _unit
                    )
                    setValue('units', updatedUnits)
                  }}
                  value={entranceOptions.find(
                    (option) => option.value === value
                  )}
                />
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name={`units.${index}.unitIdOrTitle`}
            render={({ field: { value, name, onChange } }) => (
              <FormControl
                label={translate('registerView.unit.labels.unit', {
                  context: i18nSocietyType(society.societyTypeDisplayName),
                })}
                error={
                  errors.units?.[index]?.entranceId &&
                  getErrorMessage(errors.units[index]?.unitIdOrTitle)
                }
                name={name}
              >
                <SelectDropdown
                  options={availableUnitsForWatchedUnit[index]}
                  value={availableUnitsForWatchedUnit[index].find(
                    (option) => option.value === value
                  )}
                  onChange={(option) => onChange(option?.value)}
                />
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name={`units.${index}.unitRole`}
            render={({ field: { value, name, onChange } }) => (
              <FormControl
                label={translate('common.form.labels.role')}
                error={
                  errors.units?.[index]?.entranceId &&
                  getErrorMessage(errors.units[index]?.unitRole)
                }
                name={name}
              >
                <SelectDropdown
                  options={unitRoleOptions}
                  value={unitRoleOptions.find(
                    (option) => option.value === value
                  )}
                  onChange={(option) => onChange(option?.value)}
                />
              </FormControl>
            )}
          />
          {index > 0 && (
            <Button
              label={translate('common.actions.delete')}
              variant={ButtonVariant.DANGER}
              onClick={() => remove(index)}
            />
          )}
          <Divider className="my-2" />
        </FormSpacing>
      ))}
      <Button
        onClick={() =>
          append({
            _id: undefined,
            societyId: society._id,
            entranceId:
              societyEntrances && societyEntrances?.length > 0
                ? societyEntrances[0]._id
                : undefined,
            unitIdOrTitle: undefined as unknown as string,
            userId: user._id,
            unitRole: UnitRole.MEMBER,
          })
        }
        label={translate('common.actions.add')}
      />

      <div className="mt-2 flex items-center justify-end gap-4">
        <Button
          disabled={!isValid || loading}
          variant={ButtonVariant.PRIMARY}
          label={translate('common.actions.save')}
          type="submit"
          loading={loading}
          onClick={handleSubmit(_onSubmit, onError)}
        />
      </div>
    </FormSpacing>
  )
}
